import { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { toArabicWord } from "number-to-arabic-words/dist/index-node.js";
import { toWords } from "number-to-words";
import { useReactToPrint } from "react-to-print";

// Components
import PageWrapper from "../../components/PageWrapper";
import Dropdown from "../../components/Dropdown";
import DiscountSection from "../../components/DiscountSection";
import CustomInputWithUnit from "../../components/CustomInputWithUnit";
import ReceiptTemplate from "../cashier-system/ReusableCashierReceipt";
import CustomInput from "../../components/CustomInput";
import CustomButton from "../../components/CustomButton";

// Utils
import useLanguage from "../../utils/useLanguage";
import { UserAuth } from "../../utils/AuthContext";
import { sanitizeNumber } from "../../utils/sanitizeNumber";
import saveIcon from "../../assets/save_icon.png";
import classes from "../../components/Modal.module.css";
import FabricListedTable from "./FabricListedTable";
import Loader from "../../components/Loader";
import HotCustomInput from "../../components/HotCustomInput";
import { DataContext } from "../../utils/DataContext";
import { formatDate } from "../../utils/toDate";
import PageHeadline from "../../components/PageHeadline";

const FabricCustomersCashier = ({ data }) => {
  const { t } = useTranslation("general");
  const { userData } = useContext(UserAuth);
  const { currentDirection } = useLanguage();
  const { getCustomers } = useContext(DataContext);

  // States
  const [errorFields, setErrorFields] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingProductName, setLoadingProductName] = useState(false);
  const [loadingInvoiceNumber, setLoadingInvoiceNumber] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [sumOfTotalAmount, setSumOfTotalAmount] = useState(0);
  const [originalPrice, setOriginalPrice] = useState(0);
  const [price, setPrice] = useState(0);

  const spanStyles = currentDirection === "rtl" ? classes.shortSpan : null;

  // Receipt reference for printing
  const receiptRef = useRef(null);

  // Invoice related states
  const [selectedInvoiceType, setSelectedInvoiceType] = useState({
    title: t("cash"),
    value: "cash",
  });

  const [invoice, setInvoice] = useState({
    id: "",
    date: new Date(),
    discount: 0,
    receivedAmount: 0,
    leftAmount: 0,
    notes: "",
    documentPath: "",
  });

  // Customer state
  const [selectedCustomer, setSelectedCustomer] = useState({
    id: "",
    name: "",
    phoneNumber: "",
    address: "",
  });

  const [selectedCurrency, setSelectedCurrency] = useState({
    title: "iqd",
    value: "IQD",
  });

  // Table columns configuration
  const columns = [
    { key: "colorCode", title: "colorCode", keepOnMobile: true },
    { key: "colorName", title: "colorName", keepOnMobile: true },
    { key: "quantity", title: "quantity", keepOnMobile: false },
    { key: "price", title: "price", keepOnMobile: true },
    { key: "totalAmount", title: "totalAmount", keepOnMobile: true },
  ];

  // Currency options
  const currencies = [
    { title: "iqd", value: "IQD" },
    { title: "usd", value: "USD" },
  ];

  // Print functionality
  const handlePrint = useReactToPrint({
    contentRef: receiptRef,
    pageStyle: `
      @page {
        size: 80mm;
        margin: 0;
      }
      @media print {
        body {
          width: 80mm;
          margin: 0;
          padding: 0;
          height: 100%;
        }
      }
    `,
    onAfterPrint: () => console.log("Printed receipt successfully"),
    removeAfterPrint: true,
  });

  // Field validation
  const validateFields = () => {
    setIsSubmitting(true);
    let errors = {};
    let hasError = false;

    if (!invoice.date) {
      errors["date"] = t("validDateRequired");
      hasError = true;
    }

    const rowsWithData = tableData.filter(
      (item) =>
        item.colorCode ||
        item.colorName ||
        item.quantity ||
        item.price ||
        item.totalAmount
    );

    if (rowsWithData.length === 0) {
      errors["tableData"] = t("atLeastOneItemRequired");
      hasError = true;
    } else {
      // Check if any row with data has missing fields
      const incompleteRows = rowsWithData.filter(
        (item) =>
          !(
            item.colorCode &&
            item.colorName &&
            item.quantity &&
            item.price &&
            item.totalAmount
          )
      );

      if (incompleteRows.length > 0) {
        errors["tableItems"] = t("pleaseCompleteRowData");
        hasError = true;
      }
    }

    setErrorFields(errors);
    return !hasError;
  };

  // Fetch color name by code
  const fetchColorName = async (colorCode, rowIndex, currentRows, setRows) => {
    try {
      setLoadingProductName(true);

      const response = await axios.post(
        `${process.env.REACT_APP_URL}/sellFabricColor/getColorName`,
        { colorCode },
        {
          headers: {
            Authorization: `bearer ${userData?.token}`,
            "Organization-Id": userData?.organizationId,
          },
        }
      );

      if (response.data) {
        const updatedRows = [...currentRows];
        updatedRows[rowIndex] = {
          ...updatedRows[rowIndex],
          colorName: response.data.colorName,
          price: response.data.price || 0,
         
          fabricRulerId: response.data.fabricRulerId,
        };

        setErrorFields({});

        setRows(updatedRows);
      }
    } catch (error) {
      console.error("Failed to fetch color name:", error);
      // Handle error case
      const updatedRows = [...currentRows];
      updatedRows[rowIndex] = {
        ...updatedRows[rowIndex],
        colorCode: "",
        colorName: "",
        price: 0,
        quantity: 0,
        totalAmount: 0,
      };
      setRows(updatedRows);
      setTableData(updatedRows);

      // Show error message
      setErrorFields((prev) => ({
        ...prev,
        tableItems: t("colorNotFound"),
      }));
    } finally {
      setLoadingProductName(false);
    }
  };

  // Get new invoice number
  const getNewInvoiceNumber = async () => {
    try {
      setLoadingInvoiceNumber(true);
      const response = await axios.get(
        `${process.env.REACT_APP_URL}/sellFabricColor`,
        {
          params: {
            organizationId: userData?.organizationId,
          },
        }
      );

      setInvoice((prev) => ({
        ...prev,
        id: response?.data?.invoiceNumber,
        documentPath: response?.data?.document?.[0]?.path,
      }));
    } catch (error) {
      console.error("Failed to get a new invoice number");
    } finally {
      setLoadingInvoiceNumber(false);
    }
  };

  const isRowEmpty = (row) => {
    return (
      !row.colorCode &&
      !row.colorName &&
      !row.quantity &&
      !row.price &&
      !row.totalAmount
    );
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setInvoice((prev) => ({ ...prev, [name]: value }));

    if (name === "phoneNumber" || name === "address") {
      setSelectedCustomer((prev) => ({ ...prev, [name]: value }));
    }

    if (errorFields[name]) {
      setErrorFields((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  // Create invoice
  const handleCreateInvoice = async () => {
    if (!validateFields()) {
      return;
    }

    try {
      setIsLoading(true);

      // Filter out empty rows
      const filteredTableData = tableData.filter(
        (item) =>
          item.colorCode &&
          item.colorName &&
          item.quantity &&
          item.price &&
          item.totalAmount
      );

      const payload = {
        // Invoice identifier
        id: invoice.id,

        // Customer info
        customerName: selectedCustomer.name,
        customerPhoneNumber: selectedCustomer.phoneNumber,
        customerAddress: selectedCustomer.address,

        // Invoice details
        date: new Date(invoice.date),
        notes: invoice.notes,
        totalPrice: originalPrice || 0,
        finalAmount: parseFloat(price) || 0,
        receivedAmount:
          sanitizeNumber(parseFloat(invoice?.receivedAmount)) || 0,
        leftAmount: parseFloat(sanitizeNumber(invoice?.leftAmount)) || 0,
        discount:
          sanitizeNumber(
            parseFloat(parseInt(originalPrice) - parseInt(price))
          ) || 0,
        finalAmountInAr: toArabicWord(price || 0),
        finalAmountInEn: toWords(price || 0),
        invoiceType: selectedInvoiceType.value,
        measuringUnit: "METER",

        // Colors data
        colors: filteredTableData.map((item) => ({
          colorCode: item.colorCode,
          colorName: item.colorName,
          quantity: parseFloat(item.quantity) || 0,
          price: parseFloat(item.price) || 0,
          fabricRulerId: item.fabricRulerId,
          totalAmount: parseFloat(item.totalAmount) || 0,
          measuringUnit: "METER",
          currency: selectedCurrency.value,
        })),

        // IDs
        userId: parseInt(userData?.userId),
        organizationId: parseInt(userData?.organizationId),
        customerId: selectedCustomer?.id || null,
      };

      const response = await axios.post(
        `${process.env.REACT_APP_URL}/sellFabricColor`,
        payload,
        {
          headers: {
            Authorization: `bearer ${userData?.token}`,
          },
        }
      );

      if (response.status === 201) {
        // Reset states after successful creation
        setTableData([]);
        setSelectedCustomer({
          id: "",
          name: "",
          phoneNumber: "",
          address: "",
        });
        setInvoice({
          id: "",
          date: new Date(),
          discount: 0,
          receivedAmount: 0,
          leftAmount: 0,
          notes: "",
          documentPath: "",
          address: "",
        });
        setErrorFields({});
        setIsSubmitting(false);

        // Get new invoice number and print receipt
        getNewInvoiceNumber();
        handlePrint();
      }
    } catch (error) {
      console.error("Failed to create invoice:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle save and print action
  const handleSaveAndPrint = async () => {
    await handleCreateInvoice();
  };

  const handleSelectCustomer = (customer) => {
    setSelectedCustomer(customer);
    // Clear related error messages when customer is selected
    if (customer.name) {
      setErrorFields((prev) => ({
        ...prev,
        customerName: undefined,
      }));
    }
    if (customer.phoneNumber) {
      setErrorFields((prev) => ({
        ...prev,
        phoneNumber: undefined,
      }));
    }
  };

  // Effect to calculate total amount when table data changes
  useEffect(() => {
    const totalSum = tableData.reduce((sum, item) => {
      return sum + (parseFloat(item.totalAmount) || 0);
    }, 0);

    setSumOfTotalAmount(totalSum);
    setOriginalPrice(totalSum);
    setPrice(totalSum);

    setInvoice((prev) => ({
      ...prev,
      receivedAmount: totalSum,
      leftAmount: 0,
    }));

    if (tableData.length > 0 && errorFields.tableData) {
      setErrorFields((prev) => ({
        ...prev,
        tableData: undefined,
      }));
    }
  }, [tableData]);

  useEffect(() => {
    getNewInvoiceNumber();
  }, []);

  // Initialize table data if items exist in data prop
  useEffect(() => {
    if (selectedCustomer?.id) {
      setSelectedCurrency({
        title: selectedCustomer?.currency?.toLowerCase(),
        value: t(selectedCustomer?.currency),
      });
    }
  }, [selectedCustomer]);

  return (
    <PageWrapper withBackButton>
      <div className="o-column u-padding--bottom_500">
        <PageHeadline
          title={"🛒 " + t("customersCashier")}
          classList="u-margin--top_700 u-margin--bottom_500"
        />

        {/* THE NEW ONE */}
        <section className="o-row o-row--horizontal-align_space-between">
          {/* Column 1 */}
          <div className="o-row o-row--gap-size_300">
            <div className="o-column o-column--gap-size_500 u-padding--top_100">
              <span>{t("customerName")}: </span>
              <span>{t("phoneNumber")}: </span>
              <span>{t("address")}: </span>
              <span className={spanStyles}>{t("currency")}: </span>
            </div>

            <div className="o-column o-column--gap-size_300 o-column--horizontal-align_start">
              <div className="u-width_full-width">
                <HotCustomInput
                  initOptions={[]}
                  selected={selectedCustomer}
                  setSelected={(customer) =>
                    handleSelectCustomer(customer, "noRequest")
                  }
                  optionsName={"customers"}
                  getData={getCustomers}
                  flex
                  errorText={errorFields["customerName"]}
                />
              </div>

              <div className="u-width_full-width">
                <CustomInput
                  name="phoneNumber"
                  value={selectedCustomer.phoneNumber}
                  onChange={handleInputChange}
                  errorText={errorFields["phoneNumber"]}
                  disabled={selectedCustomer?.id}
                />
              </div>
              <div className="u-width_full-width">
                <CustomInput
                  name="address"
                  value={selectedCustomer.address}
                  onChange={handleInputChange}
                  errorText={errorFields["address"]}
                  disabled={selectedCustomer?.id}
                />
              </div>
              <div className="u-width_full-width">
                <Dropdown
                  options={currencies}
                  selected={selectedCurrency}
                  setSelected={setSelectedCurrency}
                  flex
                  disabled={selectedCustomer?.id}
                />
              </div>
            </div>
          </div>
          {/* Column 2 */}
          <div className="o-row o-row--gap-size_300">
            <div className="o-column o-column--gap-size_500 u-padding--top_100">
              <span>{t("invoiceNumber")}: </span>
              <span>{t("Date")}: </span>
              <span className={spanStyles}>{t("invoiceType")}: </span>
            </div>
            <div className="o-column o-row--gap-size_300">
              <div className="u-width_full-width">
                <CustomInput
                  value={invoice?.id}
                  loading={loadingInvoiceNumber}
                />
              </div>
              <div className="u-width_full-width">
                <CustomInput
                  type="date"
                  name="date"
                  value={formatDate(invoice?.date)}
                  onChange={handleInputChange}
                  errorText={errorFields["date"]}
                />
              </div>

              <div className="u-width_full-width">
                <Dropdown
                  options={[
                    { title: t("cash"), value: "cash" },
                    { title: t("debt"), value: "debt" },
                  ]}
                  selected={selectedInvoiceType}
                  setSelected={setSelectedInvoiceType}
                  flex
                />
              </div>
            </div>
          </div>
        </section>

        {/* Table section */}
        <section className="u-margin--top_500 u-margin--bottom_500">
          {errorFields["tableData"] && (
            <div
              className="o-row u-margin--top_200 u-margin--bottom_100"
              style={{ color: "red" }}
            >
              {errorFields["tableData"]}
            </div>
          )}
          {errorFields["tableItems"] && (
            <div
              className="o-row u-margin--top_100 u-margin--bottom_300"
              style={{ color: "red" }}
            >
              {errorFields["tableItems"]}
            </div>
          )}

          <FabricListedTable
            columns={columns}
            initialData={tableData}
            handleAddRow={(newItems) => {
              const emptyRows = newItems.filter(isRowEmpty);

              if (emptyRows.length <= 1) {
                setTableData(newItems);
              }
            }}
            onFetchColorName={fetchColorName}
            loadingColorName={loadingProductName}
            handleSelectCurrency={setSelectedCurrency}
            selectedCurrency={selectedCurrency}
          />
        </section>

        {/* Price and payment section */}
        <section className="u-margin--bottom_200 o-column o-column--gap-size_400 u-max-width_500">
          <section className={classes.section}>
            <DiscountSection
              originalPrice={sumOfTotalAmount}
              onPriceChange={(priceValue) => {
                setPrice(priceValue);
                setInvoice((prev) => ({
                  ...prev,
                  receivedAmount: priceValue,
                  leftAmount: 0,
                }));
              }}
              inputMaxWidth={true}
              selectedCurrency={selectedCurrency?.value}
            />
          </section>
          <div>
            <span>{t("received")}: </span>
            <CustomInputWithUnit
              type="text"
              name="receivedAmount"
              onChange={(e) => {
                handleInputChange(e);
                setInvoice((prev) => ({
                  ...prev,
                  leftAmount: price - e.target.value,
                }));
              }}
              value={invoice.receivedAmount || price}
              unit={selectedCurrency?.title}
              min={0}
            />
          </div>
          <div>
            <span>{t("left")}: </span>
            <CustomInputWithUnit
              name="leftAmount"
              readonly={true}
              onChange={handleInputChange}
              value={
                price - invoice?.receivedAmount >= 0
                  ? (price - invoice?.receivedAmount).toFixed(2)
                  : 0
              }
              unit={selectedCurrency?.title}
            />
          </div>

          <section>
            <span>{t("notes")}: </span>
            <CustomInput
              type="textarea"
              name="notes"
              onChange={handleInputChange}
              value={invoice?.notes || ""}
            />
          </section>
        </section>

        {/* Loading indicator */}
        {isLoading && <Loader />}

        {/* Action buttons */}
        <section className="o-row o-row--horizontal-align_end o-row--gap-size_500 u-margin--top_200">
          <CustomButton
            title={t("saveAndPrint")}
            onClick={handleSaveAndPrint}
            icon={saveIcon}
            iconSize={16}
          />
        </section>

        {/* Hidden receipt template for printing */}
        <ReceiptTemplate
          ref={receiptRef}
          title="فاتورة"
          headerImage={invoice?.documentPath}
          headerText=" "
          invoiceNumber={invoice?.id}
          invoiceDate={
            invoice?.date
              ? new Date(invoice.date).toLocaleDateString("en-UK")
              : ""
          }
          receivedAmount={invoice.receivedAmount}
          leftAmount={
            price - invoice?.receivedAmount > 0
              ? (price - invoice?.receivedAmount).toFixed(2)
              : 0
          }
          userData={userData}
          items={tableData.map((item) => ({
            name: item.colorName,
            quantity: item.quantity,
            price: item.price,
          }))}
          subtotal={sumOfTotalAmount}
          discount={(sumOfTotalAmount - price).toFixed(2)}
          total={price}
          showThankYou={true}
          showArabicTotal={true}
          time={new Date()}
        />
      </div>
    </PageWrapper>
  );
};

export default FabricCustomersCashier;
